import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 0.75rem 1rem 1.5rem;
`;

const Header = styled.div`
  margin-bottom: 1.1rem;
`;

const Title = styled.h1`
  font-size: 1.45rem;
  color: ${(props) => props.theme.primaryText};
  margin-bottom: 0.25rem;
  transition: color 0.3s ease;
`;

const Subtitle = styled.p`
  color: ${(props) => props.theme.secondaryText};
  font-size: 0.875rem;
  margin: 0;
  transition: color 0.3s ease;
`;

const NavStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 0;
`;

const SectionTitle = styled.h2`
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => props.theme.mutedText};
  margin: 0;
  transition: color 0.3s ease;
`;

const NavPanel = styled.nav`
  background-color: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: ${(props) => props.theme.shadow};
`;

const NavRow = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0.85rem;
  text-decoration: none;
  color: inherit;
  border-bottom: 1px solid ${(props) => props.theme.border};
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) => props.theme.tableRowHoverBg};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.primary};
    outline-offset: -2px;
  }
`;

const NavRowText = styled.div`
  min-width: 0;
  flex: 1;
`;

const NavRowTitle = styled.span`
  display: block;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${(props) => props.theme.primaryText};
  transition: color 0.3s ease;
`;

const NavRowDesc = styled.span`
  display: block;
  font-size: 0.75rem;
  line-height: 1.35;
  color: ${(props) => props.theme.mutedText};
  margin-top: 0.1rem;
  transition: color 0.3s ease;
`;

const NavChevron = styled.span`
  flex-shrink: 0;
  color: ${(props) => props.theme.mutedText};
  font-size: 1.1rem;
  line-height: 1;
  opacity: 0.85;
`;

const ACCOUNT_LINKS = [
  {
    to: '/profile',
    title: 'Profile',
    desc: 'Account, email, and display name',
  },
  {
    to: '/tracked-picks',
    title: 'Tracked Picks',
    desc: 'Picks you are tracking from Game Picks',
  },
];

const MODEL_PICKS_LINKS = [
  {
    to: '/game-picks',
    title: 'Game Picks',
    desc: 'AI favorable picks per game — TB, HR, hit, 2+ hit',
  },
  {
    to: '/mlb/total-bases',
    title: 'MLB Total Bases',
    desc: 'Total bases predictions from our ML model',
  },
  {
    to: '/mlb/hit',
    title: 'MLB Hit (1+)',
    desc: 'Hit predictions and probabilities',
  },
  {
    to: '/mlb/two-hits',
    title: 'MLB 2+ Hits',
    desc: 'Two-hit predictions and probabilities',
  },
  {
    to: '/mlb/hr',
    title: 'MLB Home Runs',
    desc: 'Home run predictions from our ML model',
  },
];

const RESOURCE_LINKS = [
  {
    to: '/resources/odds-calculator',
    title: 'Odds Calculator',
    desc: 'American, decimal, implied probability, payouts',
  },
  {
    to: '/resources/parlay-calculator',
    title: 'Parlay Calculator',
    desc: 'Combined legs, parlay odds, potential payout',
  },
  {
    to: '/resources/betting-strategies',
    title: 'Betting Strategies',
    desc: 'Bankroll, line shopping, and fundamentals',
  },
];

const MODEL_STATS_LINKS = [
  {
    to: '/model-stats',
    title: 'Model Stats',
    desc: 'Picks of the Day and Parlay history with results',
  },
];

function NavSection({ theme, title, items }) {
  return (
    <SectionBlock>
      <SectionTitle theme={theme}>{title}</SectionTitle>
      <NavPanel theme={theme}>
        {items.map((item) => (
          <NavRow key={item.to} to={item.to} theme={theme}>
            <NavRowText>
              <NavRowTitle theme={theme}>{item.title}</NavRowTitle>
              <NavRowDesc theme={theme}>{item.desc}</NavRowDesc>
            </NavRowText>
            <NavChevron theme={theme} aria-hidden>
              ›
            </NavChevron>
          </NavRow>
        ))}
      </NavPanel>
    </SectionBlock>
  );
}

const Dashboard = () => {
  const { theme } = useTheme();
  const { user } = useAuth();

  return (
    <Container>
      <Header>
        <Title theme={theme}>Welcome, {user?.firstName || 'User'}!</Title>
        <Subtitle theme={theme}>Your SlickPicks dashboard</Subtitle>
      </Header>

      <NavStack>
        <NavSection theme={theme} title="Account" items={ACCOUNT_LINKS} />
        <NavSection theme={theme} title="Models & picks" items={MODEL_PICKS_LINKS} />
        <NavSection theme={theme} title="Resources" items={RESOURCE_LINKS} />
        <NavSection theme={theme} title="Model performance" items={MODEL_STATS_LINKS} />
      </NavStack>
    </Container>
  );
};

export default Dashboard;
